<script setup lang="ts">

import { T, useTranslate } from '@tolgee/vue'
import { computed, ref } from 'vue'
import type { Badge, PlayerStats } from '@/types/shared/player'
import BadgeOverlay from '@/components/overlays/BadgeOverlay.vue'
import MilestoneCategory from '@/components/MilestoneCategory.vue'
import { useConfig } from '@/composables/game/config'
import type { PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { useToaster } from '@/composables/game/toasters'
import { useConfetti } from '@/composables/game/confetti'
import { useBadges } from '@/composables/game/badge'
import { usePlayer } from '@/composables/game/player'
import { formatNumberFullTruncate, formatNumberSemiTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import PurchaseMilestonesImage from '@/assets/images/emojis/emoji-money-bag.png'
import TransactionMilestonesImage from '@/assets/images/emojis/emoji-analytics.png'
import PathwaysMilestonesImage from '@/assets/images/emojis/emoji-pathways.png'
import { Tooltip as VTooltip } from 'floating-vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import { useModals } from '@/composables/modal-helper'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import { MILESTONE_TYPES, type MilestoneReward } from '@/types/milestones'
import { usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'

const { config } = useConfig()
const { startCoinAnimation } = useFlyingCoins()
const { updateState } = useFortuneWheel()
const { notify } = useToaster()
const { showConfetti } = useConfetti()
const { availableBadgesMap } = useBadges()
const { updatePlayer, totalPurchase, totalTransactions, player, totalPathways } = usePlayer()
const { t } = useTranslate()
const { setShowTransactionModal, setShowRadarTransactionModal } = useModals()
const { anyActivePathwayEvent, pathwayEvents } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()

const foundBadge = ref<Badge | null>(null)
const showBadgeOverlay = computed(() => !!foundBadge.value)

const purchaseRewards = computed<MilestoneReward[]>(() => [...config.value.game.tasks.purchase.rewards]
  .sort((a, b) => a.minAmountCents - b.minAmountCents)
  .map(reward => {
    return {
      minLimit: reward.minAmountCents,
      reward: { ...reward.reward },
    }
  },
  ),
)
const totalPurchaseTag = computed(() => `$${totalPurchase.value >= 10_000 ?
  formatNumberFullTruncate(totalPurchase.value, true) :
  formatUnshortenedNumber(totalPurchase.value, true)
}`)

const transactionRewards = computed<MilestoneReward[]>(() => [...config.value.game.tasks.tonTxCount.rewards]
  .sort((a, b) => a.minTxCount - b.minTxCount)
  .map(reward => {
    return {
      minLimit: reward.minTxCount,
      reward: { ...reward.reward },
    }
  },
  ))

const totalTransactionTag = computed(() => `${formatNumberSemiTruncate(totalTransactions.value)}`)

const pathwayRewards = computed<MilestoneReward[]>(() => [...config.value.game.tasks.pathwayCount.rewards]
  .sort((a, b) => a.minCount - b.minCount)
  .map(reward => {
    return {
      minLimit: reward.minCount,
      reward: { ...reward.reward },
    }
  },
  ))

const totalPathwaysTag = computed(() => `${formatNumberSemiTruncate(totalPathways.value)}`)

function onSuccessfulClaim(result: {
	playerStats: PlayerStats,
	fortuneWheelState: PlayerFortuneWheelState
}, reward: MilestoneReward) {
  startCoinAnimation()
  notify({ type: 'success', title: 'Rewards claimed!' })
  updatePlayer(result.playerStats)
  updateState(result.fortuneWheelState)
  setTimeout(() => {
    if (reward.reward.badgeId) {
      foundBadge.value = availableBadgesMap[reward.reward.badgeId] ?? null
    }
    if (foundBadge.value) {
      showConfetti()
    }
  }, 500)
}

const claimedPurchaseRewards = computed(() => player.value.ecommerce.claimedRewards ?? [])
const claimedTransactionRewards = computed(() => player.value.ecommerce.tonTxCountClaimedRewards ?? [])
const claimedPathwayRewards = computed(() => player.value.ecommerce.pathwayCountClaimedRewards ?? [])
</script>

<template>
  <div v-if="purchaseRewards.length || transactionRewards.length || pathwayRewards.length">
    <div class="flex flex-col gap-y-1">
      <div class="text-light-white text-[22px] leading-7 font-semibold -tracking-[0.44px]">
        <T
          key-name="quests.milestone_quests_title"
          default-value="Milestones quests"
        />
      </div>
      <div class="text-light-white/70 text-sm font-semibold -tracking-[0.28px]">
        <T
          key-name="quests.milestone_quests_description"
          default-value="Unlock bonuses for reaching milestones on Dropee."
        />
      </div>
    </div>
    <MilestoneCategory
      :rewards="purchaseRewards"
      :type="MILESTONE_TYPES.PURCHASE"
      :title="t('quests.milestone_quests.purchase.title', 'Earn rewards as you spend')"
      :description="t('quests.milestone_quests.purchase.description', 'Total purchase')"
      :tag-text="totalPurchaseTag"
      :icon="PurchaseMilestonesImage"
      :claim-limit="totalPurchase * 100"
      :claimed-rewards="claimedPurchaseRewards"
      @on-claim="onSuccessfulClaim"
    />

    <MilestoneCategory
      :rewards="transactionRewards"
      :type="MILESTONE_TYPES.TRANSACTION"
      :title="t('quests.milestone_quests.transaction.title', 'On-chain transactions')"
      :description="t('quests.milestone_quests.transaction.description', 'Total transactions')"
      :tag-text="totalTransactionTag"
      :icon="TransactionMilestonesImage"
      :claim-limit="totalTransactions"
      :claimed-rewards="claimedTransactionRewards"
      @on-claim="onSuccessfulClaim"
    >
      <template #tooltip>
        <VTooltip>
          <InfoCircleIcon class="size-4 min-w-4 text-white/[38%]" />
          <template #popper>
            <p class="text-white text-sm font-medium">
              <T
                key-name="quests.milestone_quests.transaction.tooltip"
                default-value="Unlock this bonus by completing any type of transaction on TON"
              />
              (e.g., <span
                class="text-teal-primary underline font-semibold"
                @click="setShowRadarTransactionModal(true)"
              >To the moon</span>, <span
                class="text-teal-primary underline font-semibold"
                @click="setShowTransactionModal(true)"
              >Promote TON</span>, <span
                class="text-teal-primary underline font-semibold"
                @click="router.push(routePath[RouteName.SHOP])"
              >shop purchase</span>)
            </p>
          </template>
        </VTooltip>
      </template>
    </MilestoneCategory>

    <MilestoneCategory
      :rewards="pathwayRewards"
      :type="MILESTONE_TYPES.PATHWAYS"
      :title="t('quests.milestone_quests.pathways.title', 'Pathways completion')"
      :description="t('quests.milestone_quests.pathways.description', 'Total completions')"
      :tag-text="totalPathwaysTag"
      :icon="PathwaysMilestonesImage"
      :claim-limit="totalPathways"
      :claimed-rewards="claimedPathwayRewards"
      @on-claim="onSuccessfulClaim"
    >
      <template #tooltip>
        <VTooltip popper-class="pathway-milestone-quest-tooltip">
          <InfoCircleIcon class="size-4 min-w-4 text-white/[38%]" />
          <template #popper>
            <p class="text-white text-sm font-medium">
              <T
                key-name="quests.milestone_quests.pathways.tooltip"
                default-value="Unlock this bonus by reaching step 13 on "
              />
              <span
                :class="anyActivePathwayEvent ? 'text-teal-primary underline font-semibold' : 'pointer-events-none'"
                @click="showFirstPathway(pathwayEvents)"
              >pathways</span>
              <T
                key-name="quests.milestone_quests.pathways.tooltip2"
                default-value=" on home"
              />
            </p>
          </template>
        </VTooltip>
      </template>
    </MilestoneCategory>

    <BadgeOverlay
      v-if="showBadgeOverlay"
      :class="showBadgeOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
      :name="foundBadge?.name"
      :image="foundBadge?.imageUrl"
      :show="showBadgeOverlay"
      @click="foundBadge = null"
    />
  </div>
</template>

<style scoped>
.card-slider::-webkit-scrollbar {
	display: none; /* Hide scrollbar for WebKit browsers */
}

.card-slider {
	-ms-overflow-style: none; /* Hide scrollbar for IE/Edge */
	scrollbar-width: none; /* Hide scrollbar for Firefox */
}

.text-shadow-total-purchase {
	text-shadow: 0px 0px 56px rgba(0, 0, 0, 0.50)
}
</style>
<style lang="scss">
.pathway-milestone-quest-tooltip.v-popper--theme-tooltip {
	.v-popper__inner {
		@apply max-w-[220px] text-left #{!important};
	}
}
</style>